import React, { useState } from "react";
import { DataTable, Column } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Edit, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { StaffSchedule } from "@/types/staff-schedule";
import { useDeleteStaffSchedule } from "@/hooks/useStaffSchedule";
import {
  formatPattern,
  formatEffectivePeriod,
  formatHours,
} from "../utils/formatSchedule";
import { ScheduleDeleteDialog } from "./ScheduleDeleteDialog";
import { getUserFriendlyErrorMessage } from "@/utils/httpErrors";

interface ScheduleTableProps {
  data: StaffSchedule[];
  isLoading: boolean;
  hotelId: string;
  onEdit: (schedule: StaffSchedule) => void;
  staffNames: Record<string, string>;
}

export const ScheduleTable: React.FC<ScheduleTableProps> = ({
  data,
  isLoading,
  hotelId,
  onEdit,
  staffNames,
}) => {
  const { t } = useTranslation();
  const [deleteTarget, setDeleteTarget] = useState<StaffSchedule | null>(null);
  const { mutate: deleteSchedule, isPending: isDeleting } =
    useDeleteStaffSchedule();

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteSchedule(
      { id: deleteTarget.id, hotelId },
      {
        onSuccess: () => {
          toast.success(t("staffSchedule.delete.success"));
          setDeleteTarget(null);
        },
        onError: (error) => {
          toast.error(
            getUserFriendlyErrorMessage(error, t("staffSchedule.delete.error")),
          );
        },
      },
    );
  };

  const columns: Column<StaffSchedule>[] = [
    {
      key: "staffId",
      header: t("staffSchedule.table.staff"),
      render: (_, row) => (
        <span>{staffNames[row.staff?.profileId] ?? "—"}</span>
      ),
    },
    {
      key: "recurrenceType",
      header: t("staffSchedule.table.pattern"),
      render: (_, row) => <span>{formatPattern(row, t)}</span>,
    },
    {
      key: "startTime",
      header: t("staffSchedule.table.hours"),
      render: (_, row) => <span>{formatHours(row)}</span>,
    },
    {
      key: "effectiveFrom",
      header: t("staffSchedule.table.effective"),
      render: (_, row) => <span>{formatEffectivePeriod(row, t)}</span>,
    },
    {
      key: "actions",
      header: t("staffSchedule.table.actions"),
      className: "text-center",
      render: (_, row) => (
        <div className="flex items-center justify-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            title={t("staffSchedule.table.edit")}
            onClick={() => onEdit(row)}
          >
            <Edit className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            title={t("staffSchedule.table.delete")}
            onClick={() => setDeleteTarget(row)}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <DataTable<StaffSchedule>
        data={data}
        columns={columns}
        loading={isLoading}
        withPagination={false}
        withSorting={false}
        emptyMessage={t("staffSchedule.table.empty")}
      />
      <ScheduleDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        onConfirm={handleDelete}
        isPending={isDeleting}
      />
    </>
  );
};
